import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Spin,
  Typography,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  ToolOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { announcementApi } from '../../services/admin';
import type {
  Announcement,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  AnnouncementListParams,
} from '../../types/admin';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 公告类型配置
const typeConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
  info: { color: 'blue', text: '信息', icon: <InfoCircleOutlined /> },
  warning: { color: 'orange', text: '警告', icon: <ExclamationCircleOutlined /> },
  maintenance: { color: 'default', text: '维护', icon: <ToolOutlined /> },
  feature: { color: 'green', text: '功能', icon: <StarOutlined /> },
};

// 状态配置
const statusConfig: Record<string, { color: string; text: string }> = {
  draft: { color: 'default', text: '草稿' },
  published: { color: 'success', text: '已发布' },
  withdrawn: { color: 'error', text: '已撤回' },
};

// 优先级配置
const priorityConfig: Record<number, { text: string; color: string }> = {
  1: { text: '普通', color: 'default' },
  2: { text: '重要', color: 'orange' },
  3: { text: '紧急', color: 'red' },
};

export const AnnouncementManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const params: AnnouncementListParams = {
        page,
        page_size: pageSize,
      };
      const res = await announcementApi.getAdminAnnouncements(params);
      if (res.data) {
        setAnnouncements(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      message.error('获取公告列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // 打开创建弹窗
  const handleCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 打开编辑弹窗
  const handleEdit = (record: Announcement) => {
    setEditingId(record.id);
    form.setFieldsValue({
      title: record.title,
      content: record.content,
      type: record.type,
      priority: record.priority,
    });
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      if (editingId) {
        // 更新
        const data: UpdateAnnouncementRequest = values;
        await announcementApi.updateAnnouncement(editingId, data);
        message.success('公告更新成功');
      } else {
        // 创建
        const data: CreateAnnouncementRequest = values;
        await announcementApi.createAnnouncement(data);
        message.success('公告创建成功');
      }

      setModalVisible(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      message.error('保存失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 发布公告
  const handlePublish = async (id: number) => {
    try {
      await announcementApi.publishAnnouncement(id);
      message.success('公告已发布');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to publish announcement:', error);
      message.error('发布失败');
    }
  };

  // 撤回公告
  const handleWithdraw = async (id: number) => {
    try {
      await announcementApi.withdrawAnnouncement(id);
      message.success('公告已撤回');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to withdraw announcement:', error);
      message.error('撤回失败');
    }
  };

  // 删除公告
  const handleDelete = async (id: number) => {
    try {
      await announcementApi.deleteAnnouncement(id);
      message.success('公告已删除');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      message.error('删除失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<Announcement> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => <span style={{ fontWeight: 500 }}>{title}</span>,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const config = typeConfig[type] || { color: 'default', text: type, icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: number) => {
        const config = priorityConfig[priority] || { text: '普通', color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '发布时间',
      dataIndex: 'published_at',
      key: 'published_at',
      width: 180,
      render: (time: string | null) =>
        time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) =>
        time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_: unknown, record: Announcement) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 'draft' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handlePublish(record.id)}
            >
              发布
            </Button>
          )}
          {record.status === 'published' && (
            <Button
              type="link"
              size="small"
              icon={<UndoOutlined />}
              onClick={() => handleWithdraw(record.id)}
            >
              撤回
            </Button>
          )}
          <Popconfirm
            title="确认删除"
            description="确定要删除该公告吗？此操作不可恢复。"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        {/* 标题和操作按钮 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            公告管理
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            创建公告
          </Button>
        </div>

        {/* 公告列表 */}
        <Card>
          <Table
            columns={columns}
            dataSource={announcements}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条记录`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1000 }}
          />
        </Card>

        {/* 创建/编辑弹窗 */}
        <Modal
          title={editingId ? '编辑公告' : '创建公告'}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={() => setModalVisible(false)}
          confirmLoading={submitLoading}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="title"
              label="标题"
              rules={[
                { required: true, message: '请输入公告标题' },
                { max: 100, message: '标题最多100个字符' },
              ]}
            >
              <Input placeholder="请输入公告标题" />
            </Form.Item>

            <Form.Item
              name="content"
              label="内容"
              rules={[
                { required: true, message: '请输入公告内容' },
                { max: 2000, message: '内容最多2000个字符' },
              ]}
            >
              <TextArea
                rows={6}
                placeholder="请输入公告内容"
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Form.Item
              name="type"
              label="类型"
              rules={[{ required: true, message: '请选择公告类型' }]}
              initialValue="info"
            >
              <Select placeholder="请选择公告类型">
                <Option value="info">
                  <Space>
                    <InfoCircleOutlined />
                    信息
                  </Space>
                </Option>
                <Option value="warning">
                  <Space>
                    <ExclamationCircleOutlined />
                    警告
                  </Space>
                </Option>
                <Option value="maintenance">
                  <Space>
                    <ToolOutlined />
                    维护
                  </Space>
                </Option>
                <Option value="feature">
                  <Space>
                    <StarOutlined />
                    功能
                  </Space>
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="priority"
              label="优先级"
              rules={[{ required: true, message: '请选择优先级' }]}
              initialValue={1}
            >
              <Select placeholder="请选择优先级">
                <Option value={1}>普通</Option>
                <Option value={2}>重要</Option>
                <Option value={3}>紧急</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default AnnouncementManagement;
